import React, { useState, useEffect } from 'react'
import { Grid, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import { loadCalculatorConfig, getCalculatorResults, getPdf } from '@/api/pntcalculator.api'
import { TextField } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import NumberFormat from 'react-number-format'
import { useTranslation } from 'react-i18next'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import CustomTooltip from '@/components/utils/CustomTooltip'
import CustomInfoIcon from '@/components/icons/CustomInfoIcon'
import { List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import CircleIcon from '@mui/icons-material/Circle'
import Autocomplete from '@mui/material/Autocomplete'
import { useIsMerchant } from '@/config/utils/RolesUtils'
import WrapperComponentControl from '@/components/permission-control/WrapperComponentControl'
import rolesConstants from '@/config/constants/RolesConstants'
import enterpriseApi from '@/api/insenterprise.api'
import { debounce } from 'lodash'
import AsyncSelect from 'react-select/async'
import { components } from 'react-select'
import CtmCalculatorHints from './CtmCalculatorHints'
import { CustomDatePicker } from '@/components/utils/CustomDatePicker'
import dayjs from 'dayjs'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { customerFundingTypesList, customerFundingTypes } from '@/config/constants/FundingConstants'
import { useTheme } from '@mui/material'
import useClasses from '@/components/hooks/UseClasses'
import { Calculate } from '@mui/icons-material'
import { actions as loaderActions } from '@/actions/loader.actions'
import { useSelector, useDispatch } from 'react-redux'

const styles = (theme) => ({
  button: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    width: '10.5rem',
    height: '2.5rem',
    borderRadius: '0.25rem',
    border: '2px solid ' + theme.palette.neutrals.n00.color + ' !important',
  },
})

const CtmCalculator = ({ component = null }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()

  const taxes = 21
  const place = 'IVA'
  const maxAmountDefault = 50000

  const [open, setOpen] = useState(false)
  const { loaderVisible } = useSelector((state) => state.loader)

  const [listProducts, setListProducts] = useState([])
  const [maxMonthsForRenove, setMaxMonthsForRenove] = useState(0)

  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedFundingLine, setSelectedFundingLine] = useState(null)
  const [selectedFundingType, setSelectedFundingType] = useState(null)
  const [renoveInstallDate, setRenoveInstallDate] = useState(null)

  const [amount, setAmount] = useState(0)
  const [amountRenove, setAmountRenove] = useState(0)
  const [amountTaxes, setAmountTaxes] = useState(0)
  const [amountFinanced, setAmountFinanced] = useState(0)

  const [results, setResults] = useState(null)
  const [disableCalculate, setDisableCalculate] = useState(false)
  const [errorHelperText, setErrorHelperText] = useState(false)
  // Hooks añadidos para el cambio navidad. Input nuevo y uso de calculadora de roles internos
  const isMerchant = useIsMerchant()
  const [enterprise, setEnterprise] = useState(null)
  const MIN_INPUT_LENGTH = 3

  async function handleSubmit() {
    if (
      amount > calculateMaxAmount() ||
      amount < 0 ||
      (selectedFundingType === customerFundingTypes.RENOVE &&
        (amount + amountRenove > calculateMaxAmount() || amount + amountRenove < 0))
    ) {
      setErrorHelperText(true)
      setDisableCalculate(true)
    } else {
      const param = {
        ivaIgic: place,
        taxes: taxes,
        amount: amount,
        amountTaxes: amountTaxes,
        amountFinanced: amountFinanced,
        duration: selectedProduct.duration,
        idfundingpkgmaster: selectedProduct.idfundingpkgmaster,
      }
      if (selectedFundingType === customerFundingTypes.RENOVE) {
        param.amountRenove = amountRenove
        param.dateinstall = dayjs(renoveInstallDate).format('MM/DD/YYYY')
      }
      dispatch(
        loaderActions.withGlobalLoader(() =>
          getCalculatorResults(param, enterprise?.identerprise).then((response) => {
            setResults(response.data)
            setErrorHelperText(false)
            setDisableCalculate(true)
          }),
        ),
      )
    }
  }

  const disableCalculateButton = () => {
    return disableCalculate || loaderVisible || !selectedFundingType || selectedProduct === null
  }

  function resetCalculator() {
    setResults(null)
    setAmount(0)
    setAmountTaxes(0)
    setAmountFinanced(0)
    setAmountRenove(0)

    setSelectedFundingLine(null)
    setSelectedProduct(null)
    setEnterprise(null)
  }

  const handleClose = () => {
    resetCalculator()
    setOpen(false)
  }

  const calculateMaxAmount = () => {
    return (selectedProduct?.maxamount ?? maxAmountDefault) / ((selectedProduct?.openingFee ?? 0) / 100 + 1)
  }

  async function handlePrint() {
    dispatch(
      loaderActions.withGlobalLoader(() =>
        getPdf(results, enterprise?.identerprise).then((response) => {
          var link = document.createElement('a')
          link.setAttribute('href', response.data)
          link.setAttribute('download', 'presupuesto.pdf')
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }),
      ),
    )
  }

  async function loadConfig() {
    dispatch(
      loaderActions.withGlobalLoader(() =>
        loadCalculatorConfig(enterprise?.identerprise).then((response) => {
          let sortedProduct = response.data.products?.sort((a, b) => {
            if (a.fundingline === 'SOLAR' && b.fundingline !== 'SOLAR') {
              return -1
            }
            if (a.fundingline !== 'SOLAR' && b.fundingline === 'SOLAR') {
              return 1
            }
            if (a.duration !== b.duration) {
              return a.duration - b.duration
            }
            return a.tin - b.tin
          })
          setListProducts(sortedProduct)
          setSelectedFundingLine(sortedProduct.map((p) => p.fundingline)[0])
        }),
      ),
    )
  }

  useEffect(() => {
    if (isMerchant && open) {
      loadConfig()
    }
  }, [open])

  useEffect(() => {
    setMaxMonthsForRenove(getMaxProductDurationRenove())
  }, [renoveInstallDate])

  async function loadEnterprises(querysearch, callback) {
    if (querysearch.length < MIN_INPUT_LENGTH) {
      return callback([])
    }
    let params = { size: 5, querysearch: 'active:1' }
    if (querysearch) {
      params = {
        ...params,
        querysearch: ['namecom:contains(' + querysearch + ')', 'active:1'],
        size: 5,
      }
    }
    // Aunque el IDE diga que este await diga que no es necesario, si lo es. AL pasar al loaderGlobal, petaba por undefined si no está el await.
    const response = await dispatch(loaderActions.withGlobalLoader(() => enterpriseApi.loadList(params)))
    return callback(
      response?.data?.resultList.map((r) => ({
        value: r.identerprise,
        label: r.namecom,
        identerprise: r,
      })),
    )
  }

  const loadOptions = (inputValue, callback) => {
    loadEnterprises(inputValue, callback)
  }

  const debounceOptions = debounce(loadOptions, 500)

  useEffect(() => {
    if (!isMerchant && enterprise) {
      loadConfig(enterprise.identerprise)
    }
  }, [enterprise])

  useEffect(() => {
    setAmountTaxes(amount * (taxes / 100 + 1))
    setAmountFinanced(amount * (taxes / 100 + 1) * ((selectedProduct?.openingFee ?? 0) / 100 + 1))
    setDisableCalculate(false)
    setErrorHelperText(false)
  }, [amount])

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        {props.isFocused ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </components.DropdownIndicator>
    )
  }

  const getMaxProductDurationRenove = () => {
    if (renoveInstallDate) {
      const today = dayjs()

      const adjustedDate = dayjs(renoveInstallDate).subtract(12, 'month')

      const diffInMonths = today.diff(adjustedDate, 'month')
      return 300 - diffInMonths > 240 ? 240 : 300 - diffInMonths
    } else {
      return 0
    }
  }

  const getRenoveAvailableProducts = () => {
    const availableProducts = listProducts.filter(
      (p) =>
        p.fundingline === selectedFundingLine &&
        p.fundingowner === 'CUSTOMER' &&
        p.fundingtype === selectedFundingType &&
        p.duration <= maxMonthsForRenove,
    )

    return availableProducts
  }

  const classes = useClasses(styles)
  return (
    <>
      {component !== null ? (
        <div onClick={() => setOpen(true)}>{component}</div>
      ) : (
        <Button className={classes.button} onClick={() => setOpen(true)}>
          <Calculate style={{ color: theme.palette.icon.light.color }} />
          <Typography variant="title_xxs" style={{ color: theme.palette.text.light.color }}>
            {t('CALCULATOR')}
          </Typography>
        </Button>
      )}
      <Dialog
        onClose={() => handleClose()}
        open={open}
        maxWidth={'lg'}
        slotProps={{
          paper: {
            style: { borderRadius: '1.25rem' },
          },
        }}
        autoFocus={false}
        fullWidth>
        <DialogTitle style={{ padding: '2.5rem 2rem 0' }}>
          <Grid container style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant={'title_sm'} style={{ color: 'inherit' }}>
              {t('CALCULATOR.TITLE')}
            </Typography>
            <IconButton
              style={{ float: 'right' }}
              edge="end"
              color="inherit"
              onClick={() => handleClose()}
              aria-label="close"
              size="large">
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <DialogContent style={{ padding: '2.5rem 2rem' }}>
          <Grid
            container
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '2rem',
            }}>
            <Grid
              style={{
                display: 'grid',
                gap: '1rem',
                borderRight: '1px solid ' + theme.palette.neutrals.n03.color,
                padding: '0 2rem 2rem 0',
              }}>
              <WrapperComponentControl
                rolesAccess={[
                  rolesConstants.ROLE_COMMERCIAL,
                  rolesConstants.ROLE_OPERATOR,
                  rolesConstants.ROLE_ACCOUNTANT,
                  rolesConstants.ROLE_VIEWER,
                  rolesConstants.ROLE_ADMIN,
                ]}>
                <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                  <Typography variant="body_xs_medium">{t('CALCULATOR.SEARCH.ENTERPRISE')}</Typography>
                  <AsyncSelect
                    cacheOptions
                    placeholder={t('ENTERPRISE')} // Placeholder para el campo
                    value={
                      enterprise
                        ? {
                            value: enterprise.identerprise,
                            label: enterprise.namecom,
                          }
                        : null
                    }
                    onChange={(options) => {
                      setEnterprise(options.identerprise)
                    }}
                    loadOptions={debounceOptions} // Función para cargar opciones asíncronas
                    defaultOptions={false}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: theme.palette.background.white.color,
                        borderBottomColor: state.isFocused
                          ? theme.palette.primary.main
                          : theme.palette.neutrals.n04.color,
                        '&:hover': {
                          borderBottomColor: theme.palette.primary.main,
                        },
                        padding: '0px',
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 100,
                      }),
                      indicatorSeparator: () => ({
                        display: 'none',
                      }),
                    }}
                    components={{ DropdownIndicator }}
                    noOptionsMessage={(input) =>
                      input.inputValue.length >= MIN_INPUT_LENGTH
                        ? t('SEARCHCOMP.LABEL.NOMATCH')
                        : t('SEARCH.MIN.CHARACTERS', {
                            num: MIN_INPUT_LENGTH,
                          })
                    }
                    loadingMessage={() => t('SEARCH.LOADING')}
                    required={true}
                  />
                </Grid>
              </WrapperComponentControl>
              <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                <Typography variant="body_xs_medium">{t('CALCULATOR.FINANCING.LINE')}</Typography>
                <Autocomplete
                  disabled={!isMerchant && !enterprise}
                  value={selectedFundingLine ?? ''}
                  name="financeLine"
                  id="financeLine"
                  onChange={(_, value) => {
                    setSelectedFundingLine(value)
                    setSelectedProduct(null)
                  }}
                  options={[...new Set(listProducts.map((e) => e.fundingline))].map((fundingline) => fundingline)}
                  getOptionLabel={(option) =>
                    option ? t('OPPORTUNITY.FUNDING.FINANCELINE.' + option) : t('GENERAL.SELECTSOMETHING')
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                <Typography variant="body_xs_medium">{t('CALCULATOR.FINANCING.MODE')}</Typography>
                <Autocomplete
                  disabled={!isMerchant && !enterprise}
                  value={selectedFundingType ?? ''}
                  name="financeMode"
                  id="financeMode"
                  onChange={(_, value) => {
                    setSelectedFundingType(value)
                    setSelectedProduct(null)
                  }}
                  options={customerFundingTypesList.filter((type) => type !== 'LOAN')}
                  getOptionLabel={(option) => (option ? t('FINANCING.MODE.' + option) : t('GENERAL.SELECTSOMETHING'))}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              {selectedFundingType !== customerFundingTypes.RENOVE && (
                <>
                  <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                    <Typography variant="body_xs_medium">{t('CALCULATOR.MONTHS.QUESTION')}</Typography>
                    <Autocomplete
                      disabled={!isMerchant && !selectedFundingLine && !selectedFundingType}
                      value={selectedProduct ?? ''}
                      name="product"
                      id="product"
                      onChange={(_, value) => {
                        setSelectedProduct(value)
                        setDisableCalculate(false)
                      }}
                      options={listProducts.filter(
                        (p) =>
                          p.fundingline === selectedFundingLine &&
                          p.fundingowner === 'CUSTOMER' &&
                          p.fundingtype === selectedFundingType,
                      )}
                      getOptionLabel={(option) =>
                        option ? option?.duration + ' ' + t('GENERAL.MONTHS') : t('GENERAL.SELECTSOMETHING')
                      }
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body_xs_medium">{t('CALCULATOR.TOTALPRICE')}</Typography>
                      <CustomTooltip
                        hasIcon={true}
                        icon={<CustomInfoIcon width="2rem" />}
                        tooltipBorderColor={theme.palette.tertiary.t01.color}
                        tooltipBackgroundColor={theme.palette.neutrals.n00.color}
                        placement={'top-end'}
                        customContent={
                          <List style={{ padding: 0 }}>
                            <ListItem key={1} style={{ padding: '0rem 0.5rem' }}>
                              <ListItemIcon
                                style={{
                                  minWidth: '1rem',
                                  alignSelf: 'flex-start',
                                }}>
                                <CircleIcon style={{ width: '0.5rem' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant={'body_xs_medium'}>
                                    {t('CALCULATOR.TOOLTIP.LIST.ITEM1')}
                                  </Typography>
                                }
                              />
                            </ListItem>
                            <ListItem key={2} style={{ padding: '0rem 0.5rem' }}>
                              <ListItemIcon
                                style={{
                                  minWidth: '1rem',
                                  alignSelf: 'flex-start',
                                }}>
                                <CircleIcon style={{ width: '0.5rem' }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography /* align='justify' */ variant={'body_xs_medium'}>
                                    {t('CALCULATOR.TOOLTIP.LIST.ITEM2')}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          </List>
                        }>
                        <CustomInfoIcon />
                      </CustomTooltip>
                    </Grid>
                    <NumberFormat
                      fullWidth
                      customInput={TextField}
                      value={amount?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      onValueChange={(values) => {
                        const { formattedValue, value } = values
                        setAmount(Number(value))
                      }}
                      onFocus={(event) => {
                        setTimeout(() => event.target.select(), 0)
                      }}
                      decimalScale={2}
                      thousandSeparator={'.'}
                      decimalSeparator={','}
                      suffix=" €"
                      variant="outlined"
                      allowNegative="false"
                      helperText={
                        <Typography
                          variant="body_xxs_medium"
                          style={{
                            color: errorHelperText ? theme.palette.text.error.color : theme.palette.text.dark.color,
                          }}>
                          {t('CALCULATOR.AMOUNTHELPERTEXT', {
                            maxAmount: (Math.floor(calculateMaxAmount() * 100) / 100).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }),
                          })}
                        </Typography>
                      }
                    />
                  </Grid>
                </>
              )}
              {selectedFundingType === customerFundingTypes.RENOVE && (
                <>
                  <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body_xs_medium">{t('CALCULATOR.PHOTOVOLTAIC.TOTALINSTALLPRICE')}</Typography>
                      <CustomTooltip
                        hasIcon={true}
                        icon={<CustomInfoIcon width="2rem" />}
                        tooltipBorderColor={theme.palette.tertiary.t01.color}
                        tooltipBackgroundColor={theme.palette.neutrals.n00.color}
                        placement={'top-end'}
                        customContent={
                          <Typography variant="body_xxs_medium" style={{ color: theme.palette.text.dark.color }}>
                            {t('CALCULATOR.PHOTOVOLTAIC.TOTALPRICE.TOOLTIP')}
                          </Typography>
                        }>
                        <CustomInfoIcon />
                      </CustomTooltip>
                    </Grid>
                    <NumberFormat
                      fullWidth
                      customInput={TextField}
                      value={amountRenove?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      onValueChange={(values) => {
                        const { formattedValue, value } = values
                        setAmountRenove(Number(value))
                      }}
                      onFocus={(event) => {
                        setTimeout(() => event.target.select(), 0)
                      }}
                      decimalScale={2}
                      thousandSeparator={'.'}
                      decimalSeparator={','}
                      suffix=" €"
                      variant="outlined"
                      allowNegative="false"
                      helperText={
                        <Typography
                          variant="body_xxs_medium"
                          style={{
                            color: errorHelperText ? theme.palette.text.error.color : theme.palette.text.dark.color,
                          }}>
                          {t('CALCULATOR.AMOUNTHELPERTEXT', {
                            maxAmount: (Math.floor(calculateMaxAmount() * 100) / 100).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }),
                          })}
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body_xs_medium">{t('CALCULATOR.PHOTOVOLTAIC.DATE')}</Typography>
                    </Grid>
                    <CustomDatePicker
                      value={renoveInstallDate}
                      onChange={(date) => {
                        setRenoveInstallDate(date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : null)
                      }}
                    />
                  </Grid>
                  <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                    <Grid style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body_xs_medium">{t('CALCULATOR.PHOTOVOLTAIC.TOTALPRICE')}</Typography>
                    </Grid>
                    <NumberFormat
                      fullWidth
                      customInput={TextField}
                      value={amount?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      onValueChange={(values) => {
                        const { formattedValue, value } = values
                        setAmount(Number(value))
                      }}
                      onFocus={(event) => {
                        setTimeout(() => event.target.select(), 0)
                      }}
                      decimalScale={2}
                      thousandSeparator={'.'}
                      decimalSeparator={','}
                      suffix=" €"
                      variant="outlined"
                      allowNegative="false"
                      helperText={
                        <Typography
                          variant="body_xxs_medium"
                          style={{
                            color: errorHelperText ? theme.palette.text.error.color : theme.palette.text.dark.color,
                          }}>
                          {t('CALCULATOR.AMOUNTHELPERTEXT', {
                            maxAmount: (Math.floor(calculateMaxAmount() * 100) / 100).toLocaleString('es-ES', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }),
                          })}
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={12} style={{ display: 'grid', gap: '1rem' }}>
                    <Typography variant="body_xs_medium">{t('CALCULATOR.MONTHS.QUESTION')}</Typography>
                    <Autocomplete
                      disabled={!isMerchant && !selectedFundingLine && !selectedFundingType && maxMonthsForRenove === 0}
                      value={selectedProduct ?? ''}
                      name="product"
                      id="product"
                      onChange={(_, value) => {
                        setSelectedProduct(value)
                        setDisableCalculate(false)
                      }}
                      options={getRenoveAvailableProducts()}
                      getOptionLabel={(option) =>
                        option ? option?.duration + ' ' + t('GENERAL.MONTHS') : t('GENERAL.SELECTSOMETHING')
                      }
                      isOptionEqualToValue={(option, value) => option === value}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                    <div
                      style={{
                        display: 'flex',
                        gap: '.5rem',
                        alignItems: 'center',
                      }}>
                      <CustomInfoIcon width={'1rem'} />
                      <Typography
                        variant="body_xxs_medium"
                        style={{
                          color: theme.palette.text.dark.color,
                        }}>
                        {t('CALCULATOR.RENOVE.INFO.MAX', {
                          max: maxMonthsForRenove,
                        })}
                      </Typography>
                    </div>
                  </Grid>
                </>
              )}

              <Grid size={12} style={{ textAlign: 'center' }}>
                <Button
                  style={{
                    width: '100%',
                    alignSelf: 'end',
                  }}
                  type="submit"
                  variant="bigPrimary"
                  disabled={disableCalculateButton()}
                  onClick={handleSubmit}>
                  {t('CALCULATOR.CALCULATE')}
                </Button>
              </Grid>
            </Grid>
            <Grid
              style={{
                display: 'grid',
                padding: '2rem',
                gap: '1rem',
                borderRadius: '0.625rem',
                backgroundColor: theme.palette.background.grayDark.color,
              }}>
              <Typography variant="body_md_semibold" align="center">
                {t('QUOTE.FINAL')}
              </Typography>
              <Typography
                variant="title_xl"
                align="center"
                style={{
                  fontSize: '3.375rem',
                  lineHeight: 'normal',
                  color: theme.palette.text.data.color,
                }}>
                {results
                  ? results.quote?.toLocaleString('es-ES', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : ''}
                {t('GENERAL.EUROMONTH')}
              </Typography>
              <Divider
                style={{
                  backgroundColor: theme.palette.neutrals.n03.color,
                  height: '0.0625rem',
                }}
                flexItem
              />
              <Grid
                size={12}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '3fr 1fr',
                  justifyContent: 'space-between',
                  gap: '.5rem',
                }}>
                <Typography variant="text_xs_medium" style={{ color: theme.palette.text.dark.color }}>
                  {t('TINTAE')}
                </Typography>
                <Typography variant="text_s_medium_bold" align="right">
                  {results ? (
                    <>
                      {results.tin?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {t('GENERAL.PERCENTAGE')}{' '}
                    </>
                  ) : (
                    '- '
                  )}
                  /{' '}
                  {results ? (
                    <>
                      {results.tae?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {t('GENERAL.PERCENTAGE')}{' '}
                    </>
                  ) : (
                    '-'
                  )}
                </Typography>
                <Typography variant="text_xs_medium" style={{ color: theme.palette.text.dark.color }}>
                  {t('FUNDINGAMOUNT')}
                </Typography>
                <Typography variant="text_s_medium_bold" align="right">
                  {results ? (
                    <>
                      {results.amountAndFee?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {t('GENERAL.EURO')}
                    </>
                  ) : (
                    '-'
                  )}
                </Typography>
                <Typography variant="text_xs_medium" style={{ color: theme.palette.text.dark.color }}>
                  {t('CANCELFEE')}
                </Typography>
                <Typography variant="text_s_medium_bold" align="right">
                  {results ? (
                    <>
                      {results.cancellationFee?.toLocaleString('es-ES', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {t('GENERAL.PERCENTAGE')}
                    </>
                  ) : (
                    '-'
                  )}
                </Typography>
              </Grid>
              <Divider
                style={{
                  backgroundColor: theme.palette.neutrals.n03.color,
                  height: '0.0625rem',
                }}
                flexItem
              />
              <Grid
                size={12}
                style={{
                  display: 'grid',
                  gap: '.5rem',
                }}>
                <CtmCalculatorHints
                  mode={selectedFundingType}
                  lender={selectedProduct?.idlender?.label}
                  product={selectedProduct}
                  amount={amount}
                  amountRenove={amountRenove}
                />
              </Grid>
              <Button
                style={{ width: '100%', alignSelf: 'end' }}
                type="submit"
                variant="bigOutlined"
                onClick={handlePrint}
                disabled={results === null || loaderVisible}>
                {t('CALCULATOR.PRINT')}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default CtmCalculator
